import React, {
    type FC,
    type PropsWithChildren,
    createContext,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import * as Sentry from "@sentry/react";
import type { GQL, Model } from "@binale-tech/shared";
import { toast } from "@heroui/react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage } from "react-intl";

import { AppError, AppSettingsContext } from "@domain/context/AppSettingsProvider";
import { auth } from "@domain/api/firebase/firebase";
import { createSessionId, getDeviceLabel } from "@domain/infrastructure/helpers/browser";
import { logger } from "@domain/infrastructure/logger";
import { getPrivateToken, getUserData } from "@domain/context/queries/ctxQueries.graphql";
import {
    cleanupLegacySession,
    readSingleSession,
    registerSession,
    removeOwnSession,
    revokeOtherSessions,
    subscribeConnection,
    subscribeOwnSession,
    type UserSession,
} from "@domain/api/firebase/userSessions";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";
import { userToggle2fa } from "@domain/context/mutations/userMutation.graphql";

type Controller = {
    onSelectCompany: (companyId: string) => Promise<unknown>;
    savePrivateToken: (token: string, code: string) => Promise<unknown>;
    removePrivateToken: (code: string) => Promise<unknown>;
    getPrivateToken: (code: string) => Promise<string>;
    fetchUserData: () => Promise<GQL.IUser>;
};

export const UserControlContext = createContext<Controller>({
    onSelectCompany: (companyId: string) => Promise.reject(companyId),
    savePrivateToken: (token: string) => Promise.reject(token),
    removePrivateToken: () => Promise.reject(),
    getPrivateToken: () => Promise.reject(),
    fetchUserData: () => Promise.reject(),
});

export type UserProviderCompany = Pick<
    GQL.ICompany,
    "id" | "name" | "datevNrCompany" | "datevNrConsultant" | "isLocked"
>;

export const UserContext = createContext<Model.UserInterface>({ isLoading: true, fireUser: null });
export const UserCompaniesContext = createContext<UserProviderCompany[]>([]);
const getStorageKey = (uid: string) => `selectedCompany:${uid}`;
const getSessionKey = (uid: string) => `sessionId:${uid}`;

/** Matches the "you will be logged out in 5 seconds" wording in the eviction notice. */
const EVICTION_SIGN_OUT_DELAY_MS = 5000;

/**
 * One id per browser profile per user, not one per callback.
 *
 * onAuthStateChanged fires on every app load with a restored session and on token
 * refresh - not only at sign-in. Minting a fresh id there would make every reload
 * and every new tab look like a login from somewhere else and warn the user about
 * their own session. Persisting it keeps reloads and tabs quiet while a genuinely
 * different browser or machine still gets a different id and takes the slot over.
 */
export const getOrCreateSessionId = (uid: string): string => {
    const key = getSessionKey(uid);
    try {
        const existing = localStorage.getItem(key);
        if (existing) {
            return existing;
        }
        const created = createSessionId();
        localStorage.setItem(key, created);
        return created;
    } catch {
        // Private mode or storage disabled: fall back to a per-load id. Worst case
        // the user sees the notice on reload, which is better than failing to sign in.
        return createSessionId();
    }
};

export const UserProvider: FC<PropsWithChildren> = (props) => {
    const [isLoading, setLoading] = useState(true);
    const [fireUser, setFireUser] = useState<Model.UserInterface["fireUser"]>(null);
    const [claims, setClaims] = useState<Pick<Model.UserInterface, "isAdmin" | "isAdminWrite">>({});
    const [userData, setUserData] = useState<Pick<Model.UserInterface, "is2faEnabled">>({});
    const [companies, setCompanies] = useState<UserProviderCompany[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>();
    const toastSaver = useToastSaver();
    const [toggle2fa] = useMutation(userToggle2fa);
    // client.query, not useLazyQuery: the auth listener and the company form both fetch the user, and a second
    // lazy execute would abort the first (Apollo 4), which then surfaced as a Firebase connection error.
    const apolloClient = useApolloClient();
    const fetchUser = useCallback(
        () => apolloClient.query({ query: getUserData, fetchPolicy: "no-cache" }),
        [apolloClient],
    );
    const fetchPrivateToken = useCallback(
        (code: string) => apolloClient.query({ query: getPrivateToken, variables: { code }, fetchPolicy: "no-cache" }),
        [apolloClient],
    );
    const { setAppError } = use(AppSettingsContext);

    const fetchUserData = useCallback(async () => {
        const userRes = await fetchUser();
        const { isAdmin, isAdminWrite, is2faEnabled, companies: userCompanies } = userRes.data.me;
        setClaims({ isAdmin, isAdminWrite });
        setUserData({ is2faEnabled });
        setCompanies(userCompanies);
        return userRes.data.me;
    }, [fetchUser]);

    useEffect(() => {
        const reset = () => {
            Sentry.setUser(null);
            setLoading(false);
            setFireUser(null);
            setClaims({});
            setUserData({});
            setCompanies([]);
            setSelectedCompany(null);
        };
        const showAuthNotification = (session: UserSession) => {
            const isManual = session.revokedReason === "manual";
            toast.danger(
                (
                    <span>
                        <FormattedMessage
                            id={isManual ? "app.domain.sessionRevoked.title" : "app.domain.simultaneousLogin.title"}
                        />
                    </span>
                ) as any,
                {
                    description: (
                        <div>
                            <b>
                                <FormattedMessage
                                    id={
                                        isManual
                                            ? "app.domain.sessionRevoked.heading"
                                            : "app.domain.simultaneousLogin.heading"
                                    }
                                />
                            </b>
                            <br />
                            <span>
                                <FormattedMessage
                                    id={
                                        isManual
                                            ? "app.domain.sessionRevoked.body"
                                            : "app.domain.simultaneousLogin.body"
                                    }
                                />
                            </span>
                            {session.revokedBy && (
                                <>
                                    <br />
                                    <span>
                                        <FormattedMessage
                                            id="app.domain.simultaneousLogin.device"
                                            values={{ device: session.revokedBy }}
                                        />
                                    </span>
                                </>
                            )}
                        </div>
                    ) as any,
                },
            );
        };
        let unsubscribeUser: () => void = () => {};
        let evictionTimer: ReturnType<typeof setTimeout> | null = null;

        onAuthStateChanged(auth, async (fireUserData) => {
            if (fireUserData) {
                setLoading(true);
                setFireUser(fireUserData);
                Sentry.setUser({
                    email: fireUserData.email,
                });
                try {
                    const { isAdmin, companies: userCompanies } = await fetchUserData();
                    const storageKey = getStorageKey(fireUserData.uid);
                    const persistedSavedCompany = localStorage.getItem(storageKey);
                    const hasPersistedCompany = userCompanies.map((c) => c.id).includes(persistedSavedCompany);
                    const companyId = hasPersistedCompany ? persistedSavedCompany : userCompanies?.[0]?.id;
                    setSelectedCompany(companyId);
                    localStorage.setItem(storageKey, companyId);

                    setLoading(false);

                    const uid = fireUserData.uid;
                    const sessionId = getOrCreateSessionId(uid);
                    const device = getDeviceLabel();

                    await cleanupLegacySession(uid);
                    const singleSession = await readSingleSession(uid);
                    await registerSession(uid, sessionId, device);
                    if (!isAdmin && singleSession) {
                        await revokeOtherSessions(uid, sessionId, device, "login");
                    }

                    const unsubscribeConnection = subscribeConnection((connected) => {
                        // Re-arms the presence write after a reconnect, which drops the onDisconnect handler.
                        if (connected) {
                            void registerSession(uid, sessionId, device);
                        }
                    });
                    const unsubscribeOwnSession = subscribeOwnSession(uid, sessionId, {
                        onRevoked: (session) => {
                            showAuthNotification(session);
                            // The notice promises sign-out in 5 seconds; actually do it.
                            // Guarded so repeated listener fires queue only one timer.
                            if (evictionTimer === null) {
                                evictionTimer = setTimeout(() => {
                                    void removeOwnSession(uid, sessionId);
                                    signOut(auth).catch((err) =>
                                        logger.error("sign-out after session eviction failed", err),
                                    );
                                }, EVICTION_SIGN_OUT_DELAY_MS);
                            }
                        },
                        onMissing: () => {
                            void registerSession(uid, sessionId, device);
                        },
                    });
                    unsubscribeUser = () => {
                        unsubscribeConnection();
                        unsubscribeOwnSession();
                        if (evictionTimer !== null) {
                            clearTimeout(evictionTimer);
                            evictionTimer = null;
                        }
                    };
                } catch (e) {
                    setAppError(AppError.FirebaseConnectionError);
                }
            } else {
                reset();
                unsubscribeUser();
            }
        });
        // return () => reset();
    }, [fetchUserData, setAppError]);

    const isAuthenticated = Boolean(fireUser) && !isLoading;
    const value: Model.UserInterface = useMemo(
        () => ({
            isAuthenticated,
            isLoading,
            fireUser,
            selectedCompany,
            ...claims,
            ...userData,
            isUserDataLoaded: !isLoading && isAuthenticated && Boolean(fireUser),
        }),
        [isAuthenticated, isLoading, fireUser, claims, selectedCompany, userData],
    );

    const controller: Controller = useMemo(
        () => ({
            fetchUserData,
            onSelectCompany: (id) => {
                setSelectedCompany(id);
                localStorage.setItem(getStorageKey(fireUser?.uid), id);
                return Promise.resolve();
            },
            savePrivateToken: async (token, code) => {
                // toastSaver resolves undefined on failure, so the state only flips after the server accepted the code.
                const saved = await toastSaver(() =>
                    toggle2fa({
                        variables: {
                            input: {
                                enable: true,
                                token,
                                code,
                            },
                        },
                    }),
                );
                if (saved) {
                    setUserData({ is2faEnabled: true });
                }
            },
            removePrivateToken: async (code) => {
                const removed = await toastSaver(() =>
                    toggle2fa({
                        variables: {
                            input: {
                                enable: false,
                                code,
                            },
                        },
                    }),
                );
                if (removed) {
                    setUserData({ is2faEnabled: false });
                }
            },
            getPrivateToken: async (code) => {
                const privateToken = await fetchPrivateToken(code);
                if (privateToken.data) {
                    return privateToken.data.privateToken.token;
                }
                return null;
            },
        }),
        [fireUser, toastSaver, toggle2fa, fetchUserData, fetchPrivateToken],
    );

    return (
        <UserContext value={value}>
            <UserControlContext value={controller}>
                <UserCompaniesContext value={companies}>{props.children}</UserCompaniesContext>
            </UserControlContext>
        </UserContext>
    );
};
